import { createFileRoute, Navigate } from '@tanstack/react-router'

import { useAppMode } from '#hooks/use-app-mode.ts'
import { PermissionAction, PermissionService } from '#core/entities/auth/roles/Permission.ts'
import { readGatePairs } from '#core/entities/auth/roles/permission-gate-helpers.ts'
import { useUserState } from '#core/state/UserState.ts'

export const Route = createFileRoute('/_app/$companyId/')({
  component: RouteComponent,
})

// Where the bare company root lands, by the shared landing rule:
//   • app mode (mobile) → Work Requests
//   • desktop           → Work Orders if the user can read WO, else Work Requests
// Edge guard: an app-mode user who can neither read nor create Work Requests would
// be bounced by the WR route's PermissionGuard to /not-authorized, so we send them
// to the first primary module they CAN reach (WO → Assets) before falling through
// to WR (which then shows its own not-authorized state).
function RouteComponent() {
  const params = Route.useParams()
  const appMode = useAppMode()
  const userState = useUserState()

  const canReadWorkOrders = userState.hasAnyPermission(readGatePairs(PermissionService.WORK_ORDERS))
  const canEnterWorkRequests =
    userState.hasAnyPermission(readGatePairs(PermissionService.WORK_ORDER_REQUESTS)) ||
    userState.hasPermission(PermissionService.WORK_ORDER_REQUESTS, PermissionAction.CREATE)
  const canReadAssets = userState.hasAnyPermission(readGatePairs(PermissionService.ASSETS))

  let target: '/$companyId/work-requests' | '/$companyId/work-orders' | '/$companyId/assets'
  if (appMode) {
    // Mobile prefers the Work Requests home; only step off it when the user can't enter WR.
    target = canEnterWorkRequests
      ? '/$companyId/work-requests'
      : canReadWorkOrders
        ? '/$companyId/work-orders'
        : canReadAssets
          ? '/$companyId/assets'
          : '/$companyId/work-requests'
  } else {
    // Desktop prefers Work Orders when readable, else Work Requests.
    target = canReadWorkOrders ? '/$companyId/work-orders' : '/$companyId/work-requests'
  }

  return <Navigate to={target} params={{ companyId: params.companyId }} replace />
}
